<script lang="ts">
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import {
		real_id,
		celsius_to_fahrenheit,
		fahrenheit_to_celsius,
	} from '../../../../helpers/interface_helpers';
	import { onSettingsResponse } from '../../../../helpers/device_settings_helpers';
	import { copy } from '../../../../helpers/copy_text';
	//Services
	import { postReq } from '../../../../services/http';
	//Stores
	//Design system components
	import { Checkbox, Slider, Button, Text, IconButton } from '@Shelly/shelly-design-system';
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	import type { Request } from '../../../../types/requests/request';
	import type { Device } from '../../../../types/Device';
	import { onMount } from 'svelte';
	export let device: Device;

	const id = real_id(device.id),
		ip = device.ip,
		settings = readDeviceSettings(id)?.thermostats[0],
		units = settings.target_t.units,
		saveOffset = async (): Promise<Request> => {
			const temperatureOffsetValue =
				units === 'F' ? fahrenheit_to_celsius(temperature_offset) : temperature_offset;

			if (temperatureOffsetValue == settings.temperature_offset) return { isok: true };
			return await postReq(
				`/device/generic_command`,
				{
					id,
					command: `temperature_offset`,
					payload: JSON.stringify({ value: Number(temperatureOffsetValue) }),
				},
				20000,
			);
		},
		setExternalTemp = async (): Promise<Request> => {
			if (ext_t_enabled == settings.ext_t.enabled) return { isok: true };
			return await postReq(
				`/device/generic_command`,
				{
					id,
					command: `ext_t`,
					payload: JSON.stringify({
						enabled: ext_t_enabled,
						id: device.channel,
					}),
				},
				20000,
			);
		},
		setTempAuto = async (): Promise<Request> => {
			if (ext_t_enabled || t_auto_enabled == settings.t_auto.enabled) return { isok: true };
			return await postReq(
				`/device/generic_command`,
				{
					id,
					command: 't_auto',
					payload: JSON.stringify({
						enabled: t_auto_enabled,
						id: device.channel,
					}),
				},
				20000,
			);
		},
		save = async () => {
			waitForResponse = true;
			const res: SettingRequest[] = await Promise.all([
				saveOffset(),
				setExternalTemp(),
				setTempAuto(),
			]);
			onSettingsResponse(res, device);
			waitForResponse = false;
		};

	let ext_t_enabled = settings.ext_t.enabled,
		t_auto_enabled = settings.t_auto.enabled,
		temperature_offset = settings.temperature_offset,
		min = -10,
		max = 10,
		waitForResponse = false;
	const url = `http://${ip}/ext_t?temp=`;

	const validateUnits = () => {
		if (units === 'F') {
			min = celsius_to_fahrenheit(min);
			max = celsius_to_fahrenheit(max);
			temperature_offset = celsius_to_fahrenheit(temperature_offset);
		}
	};

	onMount(() => {
		validateUnits();
	});
	const a11yProps = {
		sliderValueText: $format('current_temperature_offset_value', {
			values: { value: temperature_offset },
			default: `Current temperature offset value is ${temperature_offset}`,
		}),
		ariaLabel: $format('label_set_temperature_offset', { default: 'Set Temperature Offset' }),
		ariaLabelValue: $format('current_temperature_offset_value', {
			values: { value: temperature_offset },
			default: `Current temperature offset value is ${temperature_offset}`,
		}),
	};
</script>

<Checkbox class="mb-s" size="m" bind:checked={ext_t_enabled} id="checkbox-tkzxes3"
	>{$format('external_description_no_ip', {
		default: `Enable external temperature corrections:`,
	})}</Checkbox
>
<div class="d-flex flex-wrap mb-s">
	<div class="d-flex flex-auto justify-content-between align-items-center">
		<Text styleType={'body-small'} class={'text-small'}>{url}</Text>
		<IconButton
			icon="fas fa-copy"
			class="ml-s"
			on:click={() =>
				copy(
					url,
					$format('copied_successfully', {
						default: 'Copied successfully!',
					}),
				)}
			aria-label={$format('button_copy', { default: 'Copy' })}
		/>
	</div>
</div>

{#if !ext_t_enabled}
	<Checkbox class="mb-s" size="m" bind:checked={t_auto_enabled} id="checkbox-ko84ahd"
		>{$format('t_auto_description', {
			default:
				'Enable internal temperature compensation. This feature helps to read the temperature correctly despite the proximity of the head source.',
		})}</Checkbox
	>
{/if}

<Text styleType="body-small" class="mb-m">
	{$format('text_trv_offset_description', {
		values: {
			setting: $format(`the_temperature`, { default: 'the temperature' }),
			min,
			max,
			units,
		},
		default:
			'Offset in {units} to be applied to the measured temperature. The value must be between {min}°{units} and {max}°{units}.',
	})}
</Text>

<Slider
	id="slider-jpkynlj"
	{a11yProps}
	class="mb-m"
	disabled={waitForResponse}
	type="horizontal"
	unit={`°${units}`}
	{min}
	{max}
	bind:value={temperature_offset}
/>

<Button loading={waitForResponse} size="m" class="text-uppercase" width="full" on:click={save}
	>{$format('button_apply', { default: 'apply' })}</Button
>
